import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AssigneePicker } from "@/components/issues/AssigneePicker";
import { PanelPicker } from "@/components/action/PanelPicker";
import { TASK_PRIORITIES } from "@/constants/workflow";
import { api, apiError } from "@/lib/api";

const empty = {
  name: "",
  description: "",
  assignee_ids: [],
  start_date: "",
  due_date: "",
  priority: "Normal",
  panel_uids: [],
  phase_id: "",
};

/** Create or edit a task. Creator and creation date are recorded by the backend. */
export function TaskDialog({
  projectId,
  task,
  preset,
  users,
  panels,
  phases,
  open,
  onOpenChange,
  onSaved,
}) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(
      task
        ? {
            name: task.name || "",
            description: task.description || "",
            assignee_ids: task.assignee_ids || [],
            start_date: (task.start_date || "").slice(0, 10),
            due_date: (task.due_date || "").slice(0, 10),
            priority: task.priority || "Normal",
            panel_uids: task.panel_uids || [],
            phase_id: task.phase_id || "",
          }
        : { ...empty, ...(preset || {}) },
    );
  }, [open, task, preset]);

  const submit = async () => {
    setBusy(true);
    try {
      if (task) {
        const dirty = {};
        if (form.name !== task.name) dirty.name = form.name;
        if (form.description !== (task.description || ""))
          dirty.description = form.description;
        if (form.priority !== task.priority) dirty.priority = form.priority;
        if (form.due_date !== (task.due_date || "").slice(0, 10))
          dirty.due_date = form.due_date || null;
        if (form.start_date !== (task.start_date || "").slice(0, 10))
          dirty.start_date = form.start_date || null;
        if (form.phase_id !== (task.phase_id || "")) dirty.phase_id = form.phase_id || null;
        if (form.panel_uids.join(",") !== (task.panel_uids || []).join(","))
          dirty.panel_uids = form.panel_uids;
        if (
          form.assignee_ids.join(",") !== (task.assignee_ids || []).join(",")
        )
          dirty.assignee_ids = form.assignee_ids;
        if (Object.keys(dirty).length) {
          await api.patch(`/projects/${projectId}/tasks/${task.id}`, dirty);
        }
      } else {
        await api.post(`/projects/${projectId}/tasks`, {
          ...form,
          start_date: form.start_date || null,
          due_date: form.due_date || null,
          phase_id: form.phase_id || null,
        });
      }
      toast.success(task ? "Task updated" : "Task created");
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="task-dialog"
        className="max-w-md border-slate-200 bg-white text-slate-900"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-medium">
            {task ? "Edit task" : "Add task"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">
              Task name
            </p>
            <Input
              data-testid="task-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="What needs to be done"
              className="border-slate-200 bg-[#EEF2F8] text-sm"
            />
          </div>

          <div>
            <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">
              Description
            </p>
            <Textarea
              data-testid="task-description-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Explain the work to be completed"
              rows={3}
              maxLength={4000}
              className="resize-none border-slate-200 bg-[#EEF2F8] text-sm"
            />
          </div>

          <div>
            <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">
              Assignee (one or more)
            </p>
            <AssigneePicker
              users={users}
              value={form.assignee_ids}
              onChange={(ids) => setForm({ ...form, assignee_ids: ids })}
              testId="task-assignee-select"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">
                Start date
              </p>
              <Input
                data-testid="task-start-date-input"
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="border-slate-200 bg-[#EEF2F8] text-xs"
              />
            </div>
            <div>
              <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">
                Due date
              </p>
              <Input
                data-testid="task-due-date-input"
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="border-slate-200 bg-[#EEF2F8] text-xs"
              />
            </div>
            <div>
              <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">
                Priority
              </p>
              <Select
                value={form.priority}
                onValueChange={(value) => setForm({ ...form, priority: value })}
              >
                <SelectTrigger
                  data-testid="task-priority-select"
                  className="h-9 border-slate-200 bg-[#EEF2F8] text-xs"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-slate-200 bg-white text-slate-800">
                  {TASK_PRIORITIES.map((value) => (
                    <SelectItem key={value} value={value} className="text-xs">
                      {value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">
                Linked panels (optional)
              </p>
              <PanelPicker
                panels={panels || []}
                value={form.panel_uids}
                onChange={(uids) => setForm({ ...form, panel_uids: uids })}
              />
            </div>
            <div>
              <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">
                Phase (optional)
              </p>
              <Select
                value={form.phase_id || "none"}
                onValueChange={(value) =>
                  setForm({ ...form, phase_id: value === "none" ? "" : value })
                }
              >
                <SelectTrigger
                  data-testid="task-phase-select"
                  className="h-9 border-slate-200 bg-[#EEF2F8] text-xs"
                >
                  <SelectValue placeholder="No phase" />
                </SelectTrigger>
                <SelectContent className="border-slate-200 bg-white text-slate-800">
                  <SelectItem value="none" className="text-xs">
                    No phase
                  </SelectItem>
                  {(phases || []).map((phase) => (
                    <SelectItem key={phase.id} value={phase.id} className="text-xs">
                      {phase.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            data-testid="task-submit-button"
            onClick={submit}
            disabled={busy || form.name.trim().length < 2}
            className="rounded-full bg-cyan-600 text-white hover:bg-cyan-700"
          >
            {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
            {task ? "Save" : "Create task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
